import React, { useState } from "react";
import { api, logChuck } from "@/lib/api";
import { toast } from "sonner";
import { X, Zap, Building2, Sparkles, Loader2, Check } from "lucide-react";

const TIERS = [
  {
    code: "free",
    name: "Free",
    price: 0,
    icon: Sparkles,
    lookup_key: null,
    features: [
      "1 project",
      "AI drafting (with watermark)",
      "Live openFDA + ClinicalTrials.gov + USPTO",
      "MAUDE + FAERS radar",
      "Chuck AI (opt-in)",
    ],
    accent: "border-slate-200 bg-slate-50",
    cta: "Current plan",
    disabled: true,
  },
  {
    code: "pro",
    name: "Pro",
    price: 399,
    icon: Zap,
    lookup_key: "submitta_pro_monthly",
    highlight: true,
    features: [
      "Up to 99 projects",
      "No watermark",
      "Saved predicate sets",
      "Priority AI queue",
      "CSR / PADER / PSUR / Protocol / REMS drafters",
      "DailyMed label anchoring",
      "Email support",
    ],
    accent: "border-[#2563EB] bg-[#2563EB]/5",
    cta: "Upgrade to Pro",
  },
  {
    code: "enterprise",
    name: "Enterprise",
    price: 799,
    icon: Building2,
    lookup_key: "submitta_enterprise_monthly",
    features: [
      "Unlimited projects",
      "Reviewer roles + audit log",
      "Zero-retention model calls",
      "SSO + SCIM",
      "Dedicated onboarding",
      "SOC 2 + HIPAA posture reports",
    ],
    accent: "border-slate-300 bg-white",
    cta: "Upgrade to Enterprise",
  },
];

export default function PaywallModal({ open, onClose, quota, reason = "You've hit your Free plan limit" }) {
  const [busy, setBusy] = useState(null);

  React.useEffect(() => {
    if (open) {
      logChuck("paywall_shown", { payload: { reason, plan: quota?.plan || "free",
        projects_used: quota?.projects_used, projects_max: quota?.projects_max } });
    }
  }, [open, quota, reason]);

  if (!open) return null;

  const upgrade = async (tier) => {
    if (!tier.lookup_key) return;
    setBusy(tier.code);
    logChuck("checkout_initiated", { payload: { plan: tier.code, price_usd: tier.price } });
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key: tier.lookup_key,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not create checkout session");
        setBusy(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="paywall-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-h-[92vh] w-full max-w-5xl overflow-y-auto border border-slate-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="paywall-close"
          onClick={onClose}
          className="absolute right-4 top-4 border border-slate-200 bg-white p-1.5 text-slate-500 hover:border-slate-400 hover:text-slate-900"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="border-b border-slate-200 bg-slate-50 px-8 py-6">
          <div className="overline mb-1 text-[#2563EB]">UPGRADE REQUIRED</div>
          <h2 className="font-serif text-2xl font-semibold text-slate-900 lg:text-3xl">
            {reason}
          </h2>
          {quota && (
            <p className="mt-2 text-sm text-slate-600">
              You're on <b>{quota.plan?.toUpperCase()}</b> — {quota.projects_used} of {quota.projects_max} project
              {quota.projects_max === 1 ? "" : "s"} used. Upgrade to add more.
            </p>
          )}
        </div>

        <div className="grid gap-4 p-8 md:grid-cols-3">
          {TIERS.map((t) => {
            const I = t.icon;
            const isCurrent = t.code === (quota?.plan || "free");
            return (
              <div
                key={t.code}
                data-testid={`paywall-tier-${t.code}`}
                className={`relative flex flex-col border p-6 ${t.accent} ${t.highlight ? "shadow-md" : ""}`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-6 border border-[#2563EB] bg-[#2563EB] px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
                    Recommended
                  </div>
                )}
                <div className="mb-3 flex items-center gap-2">
                  <I className={`h-5 w-5 ${t.highlight ? "text-[#2563EB]" : "text-slate-500"}`} strokeWidth={1.5} />
                  <div className="font-serif text-xl font-semibold text-slate-900">{t.name}</div>
                </div>
                <div className="mb-4">
                  <span className="font-serif text-3xl font-semibold text-slate-900">${t.price}</span>
                  {t.price > 0 && <span className="text-sm text-slate-500">/month</span>}
                </div>
                <ul className="mb-6 flex-1 space-y-1.5 text-xs text-slate-700">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <div
                    data-testid={`paywall-cta-${t.code}`}
                    className="w-full border border-slate-200 bg-white px-4 py-2 text-center text-xs font-semibold text-slate-500"
                  >
                    Current plan
                  </div>
                ) : (
                  <button
                    data-testid={`paywall-cta-${t.code}`}
                    onClick={() => upgrade(t)}
                    disabled={busy === t.code || t.disabled}
                    className={`inline-flex w-full items-center justify-center gap-2 px-4 py-2 text-xs font-semibold transition-colors ${
                      t.highlight
                        ? "bg-[#2563EB] text-white hover:bg-[#1D4ED8] disabled:opacity-50"
                        : "border border-slate-900 bg-white text-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-50"
                    }`}
                  >
                    {busy === t.code && <Loader2 className="h-3 w-3 animate-spin" />}
                    {t.cta}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="border-t border-slate-200 bg-slate-50 px-8 py-4 text-[11px] text-slate-500">
          Secure payments via <b>Stripe</b>. Cancel anytime. All plans include Chuck AI, live openFDA, and audit-ready exports.
        </div>
      </div>
    </div>
  );
}
